import React, { useRef, useCallback, useMemo, useReducer, useEffect, Suspense } from "react";

import { useNavigation } from "@react-navigation/native";
import type { StackNavigationProp } from "@react-navigation/stack";
import {
  Modal,
  VStack,
  Text,
  Box,
  Checkbox,
  Center,
  PresenceTransition,
  HStack,
  Button,
  useToast,
} from "native-base";
import {
  useMutation,
  usePaginationFragment,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";
import { RecyclerListView, DataProvider, LayoutProvider } from "recyclerlistview";
import type { RecyclerListViewProps } from "recyclerlistview";
import { fetchQuery } from "relay-runtime";
import type { Subscription } from "relay-runtime";

import AccountSelectRow from "pianofunclub-crm/components/ListItems/AccountSelectRow";
import type { AccountModalItem } from "pianofunclub-crm/components/ListItems/AccountSelectRow";
import EnrolmentSelectRow from "pianofunclub-crm/components/ListItems/EnrolmentSelectRow";
import type { EnrolmentItem } from "pianofunclub-crm/components/ListItems/EnrolmentSelectRow";
import type { AccountsStackNavigatorParamList } from "pianofunclub-crm/navigation/AccountsNavigator";
import type { WaitingListNavigatorParamList } from "pianofunclub-crm/navigation/WaitingListNavigator";
import { getEnrolErrorToastText } from "pianofunclub-crm/utils/errorHandling";

import LoadingBlobs from "pianofunclub-shared/components/Animations/LoadingBlobs";
import ButtonDebounced from "pianofunclub-shared/components/Buttons/ButtonDebounced";
import TopTabBar from "pianofunclub-shared/components/Buttons/TopTabBar";
import TextInput from "pianofunclub-shared/components/Inputs/TextInput";
import type { TextInputRef } from "pianofunclub-shared/components/Inputs/TextInput";
import ListEmptyBanner from "pianofunclub-shared/components/ListItems/ListEmptyBanner";
import type { SelectRowRef } from "pianofunclub-shared/components/ListItems/SelectRow";
import DayPickerModal from "pianofunclub-shared/components/Modals/DayPickerModal";
import type { SelectedProfileInfo } from "pianofunclub-shared/components/Modals/UpdateLessonBlockDetailsModal";
import AlertPopup from "pianofunclub-shared/components/NativeBaseExtended/AlertPopup";
import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import CustomFlatListSpinner from "pianofunclub-shared/components/Other/CustomFlatListSpinner";
import { ChevronLeftIcon, CloseIcon } from "pianofunclub-shared/components/Other/Icons";
import SearchBar from "pianofunclub-shared/components/Other/SearchBar";
import type { LessonBlockDataNode } from "pianofunclub-shared/components/Registers/TeacherRegisters";
import type {
  EnrolPupilMutation,
  EnrolPupilMutation$data,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/EnrolPupilMutation.graphql";
import type { LoadAccountsForModal_query_profiles$key } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadAccountsForModal_query_profiles.graphql";
import type { LoadAccountsForModalPaginationQuery } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadAccountsForModalPaginationQuery.graphql";
import type { LoadAccountsForModalQuery } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadAccountsForModalQuery.graphql";
import type { LoadEnrolments_query_enrolments$key } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadEnrolments_query_enrolments.graphql";
import type { LoadEnrolmentsPaginationQuery } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadEnrolmentsPaginationQuery.graphql";
import type { LoadEnrolmentsQuery } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadEnrolmentsQuery.graphql";
import { enrol_pupil } from "pianofunclub-shared/relay/graphql/accounts/EnrolPupil";
import {
  load_accounts_for_modal,
  load_accounts_for_modal_pagination,
} from "pianofunclub-shared/relay/graphql/accounts/LoadAccountsForModal";
import {
  load_enrolments,
  load_enrolments_pagination,
} from "pianofunclub-shared/relay/graphql/accounts/LoadEnrolments";
import type {
  OfferLessonBlockToNewPupilMutation,
  OfferLessonBlockToNewPupilMutation$data,
} from "pianofunclub-shared/relay/graphql/registers/__generated__/OfferLessonBlockToNewPupilMutation.graphql";
import { offer_lesson_block_to_new_pupil } from "pianofunclub-shared/relay/graphql/registers/OfferLessonBlockToNewPupil";
import {
  getSlotOfferMessageString,
  getSlotOfferMessageSubject,
} from "pianofunclub-shared/utils/extractors";
import { useDebounceFunction } from "pianofunclub-shared/utils/hooks";
import { createReducer } from "pianofunclub-shared/utils/reducers";

interface Props extends WrapperProps {
  loadEnrolmentsQueryReference: PreloadedQuery<LoadEnrolmentsQuery, Record<string, unknown>>;
  loadPupilsQueryReference: PreloadedQuery<LoadAccountsForModalQuery, Record<string, unknown>>;
  loadTeachersQueryReference: PreloadedQuery<LoadAccountsForModalQuery, Record<string, unknown>>;
}

interface WrapperProps {
  dayIndex?: number;
  detailType?: "TEACHER" | "PUPIL" | "TEACHER_AND_DAY";
  hideModal: () => void;
  keepFree?: boolean;
  lessonBlockDataForPupilChange?: LessonBlockDataNode;
  lessonBlockIds?: string[];
  lessonBlocksConnectionId?: string;
  onUpdate?: () => void;
  schoolId?: string;
  selectedBlock?: number;
  selectedProfileInfo?: SelectedProfileInfo;
  selectedStartingYear?: number;
  showModal: boolean;
  updateLessonBlockDetails: (
    variables: {
      instrument?: string | null;
      keepFree?: boolean;
      lessonBlockIds: string[];
      pupilId?: string | null;
      staffNoteForTeacher?: string;
      stageId?: string | null;
      teacherId?: string | null;
      teacherNoteForStaff?: string;
    },
    lessonBlockCursor?: string,
    onComplete?: () => void
  ) => void;
  updateLessonBlockDetailsInFlight: boolean;
  updateScheduledLessonBlockTime?: (
    lessonBlockIds: string[],
    dayIndex: number,
    hours?: number,
    minutes?: number,
    onComplete?: () => void
  ) => void;
  updateScheduledLessonBlockTimeInFlight?: boolean;
}

type AlreadyOfferedAlertInfo = {
  actionType?: "OFFER_SLOT" | "ENROL_DIRECTLY" | "ADD_EXISTING_PUPIL" | "REMOVE_PUPIL";
  isOpen: boolean;
  type?:
    | "SLOT_ALREADY_OFFERED"
    | "PUPIL_ALREADY_OFFERED_OTHER_SLOT"
    | "SLOT_ALREADY_OFFERED_TO_THIS_PUPIL";
};

type ReducerValues = {
  alreadyOfferedAlertInfo: AlreadyOfferedAlertInfo;
  archiveExistingOfferOnOfferSlot?: boolean;
  contentIsRendered: boolean;
  currentPageIndex: number;
  currentTabIndex: number;
  dataProvider: DataProvider;
  dayPickerAfterTeacherIsOpen?: boolean;
  isRefetching: boolean;
  keepFree?: boolean;
  messageString?: string;
  messageSubject?: string;
  searchTerm: string;
  selectedProfileInfo?: SelectedProfileInfo;
  subscription?: Subscription;
};

const LOAD_X_PROFILES = 12;

const UpdateLessonBlockTeacherOrPupilModal = (props: Props) => {
  const {
    dayIndex,
    detailType,
    hideModal,
    keepFree,
    lessonBlockDataForPupilChange,
    lessonBlockIds,
    lessonBlocksConnectionId,
    loadEnrolmentsQueryReference,
    loadPupilsQueryReference,
    loadTeachersQueryReference,
    onUpdate,
    schoolId,
    selectedBlock,
    selectedProfileInfo: initiallySelectedProfileInfo,
    selectedStartingYear,
    showModal,
    updateLessonBlockDetails,
    updateLessonBlockDetailsInFlight,
    updateScheduledLessonBlockTime,
    updateScheduledLessonBlockTimeInFlight,
  } = props;

  const navigation =
    useNavigation<
      StackNavigationProp<AccountsStackNavigatorParamList | WaitingListNavigatorParamList>
    >();

  const detailTypeText =
    detailType === "TEACHER" || detailType === "TEACHER_AND_DAY" ? "Teacher" : "Pupil";

  const initialState = useMemo(() => {
    return {
      values: {
        alreadyOfferedAlertInfo: {
          isOpen: false,
        },
        contentIsRendered: false,
        currentPageIndex: 0,
        currentTabIndex: initiallySelectedProfileInfo?.enrolmentStatus ? 1 : 0,
        dataProvider: new DataProvider((r1, r2) => {
          return r1 !== r2;
        }),
        isRefetching: false,
        keepFree: keepFree,
        lessonBlockIds,
        searchTerm: "",
        selectedProfileInfo: initiallySelectedProfileInfo,
        subscription: undefined,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reducer = createReducer<ReducerValues>(initialState);
  const [state, dispatchState] = useReducer(reducer, initialState);

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    dispatchState({
      input: "selectedProfileInfo",
      value: initiallySelectedProfileInfo,
    });
    rowRefs.current.forEach((ref, id) => {
      ref?.setIsChecked(id === initiallySelectedProfileInfo?.id);
    });
  }, [initiallySelectedProfileInfo]);

  const loadAccountsForModalResponse = usePreloadedQuery(
    load_accounts_for_modal,
    detailType === "TEACHER" || detailType === "TEACHER_AND_DAY"
      ? loadTeachersQueryReference
      : loadPupilsQueryReference
  );

  const loadEnrolmentsResponse = usePreloadedQuery(load_enrolments, loadEnrolmentsQueryReference);

  const accountsData = usePaginationFragment<
    LoadAccountsForModalPaginationQuery,
    LoadAccountsForModal_query_profiles$key
  >(load_accounts_for_modal_pagination, loadAccountsForModalResponse);

  const enrolmentsData = usePaginationFragment<
    LoadEnrolmentsPaginationQuery,
    LoadEnrolments_query_enrolments$key
  >(load_enrolments_pagination, loadEnrolmentsResponse);

  const [commitEnrolPupil, enrolPupilInFlight] = useMutation<EnrolPupilMutation>(enrol_pupil);

  const [commitOfferLessonBlockToNewPupil, offerLessonBlockToNewPupilInFlight] =
    useMutation<OfferLessonBlockToNewPupilMutation>(offer_lesson_block_to_new_pupil);

  const toast = useToast();

  const messageSubjectInputRef = useRef<TextInputRef>(null);
  const messageStringInputRef = useRef<TextInputRef>(null);

  const rowRefs = useRef(new Map<string, SelectRowRef>());

  useEffect(() => {
    rowRefs.current = new Map();

    if (state.values.currentTabIndex == 0) {
      if (accountsData.data.profiles?.edges && accountsData.data.profiles.edges.length > 0) {
        dispatchState({
          input: "dataProvider",
          value: state.values.dataProvider.cloneWithRows([...accountsData.data.profiles.edges]),
        });
        // if there is no data, create a fresh DataProvider
      } else {
        dispatchState({
          input: "dataProvider",
          value: new DataProvider((r1, r2) => {
            return r1 !== r2;
          }),
        });
      }
    } else if (state.values.currentTabIndex == 1) {
      if (
        enrolmentsData.data.enrolments?.edges &&
        enrolmentsData.data.enrolments?.edges.length > 0
      ) {
        dispatchState({
          input: "dataProvider",
          value: state.values.dataProvider.cloneWithRows([...enrolmentsData.data.enrolments.edges]),
        });
        // if there is no data, create a fresh DataProvider
      } else {
        dispatchState({
          input: "dataProvider",
          value: new DataProvider((r1, r2) => {
            return r1 !== r2;
          }),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.values.currentTabIndex, enrolmentsData.data, accountsData.data]);

  const layoutProvider = useMemo(() => {
    return new LayoutProvider(
      () => "VSEL",
      (type, dim) => {
        switch (type) {
          case "VSEL":
            dim.width = 800;
            dim.height = 43;
            break;
          default:
            dim.width = 0;
            dim.height = 0;
        }
      }
    );
  }, []);

  const onHideModal = useCallback(() => {
    hideModal();
    dispatchState({
      input: "archiveExistingOfferOnOfferSlot",
      value: false,
    });
    dispatchState({
      input: "searchTerm",
      value: "",
    });
    dispatchState({
      input: "currentPageIndex",
      value: 0,
    });
    dispatchState({
      input: "selectedProfileInfo",
      value: undefined,
    });
    dispatchState({
      input: "keepFree",
      value: undefined,
    });
    dispatchState({
      input: "messageString",
      value: undefined,
    });
    dispatchState({
      input: "messageSubject",
      value: undefined,
    });
    rowRefs.current.forEach((ref) => ref?.setIsChecked(false));
  }, [hideModal, dispatchState]);

  const onSearchEndReached = useCallback(() => {
    if (state.values.currentTabIndex == 0 && accountsData.hasNext && !accountsData.isLoadingNext) {
      accountsData.loadNext(LOAD_X_PROFILES);
    }

    if (
      state.values.currentTabIndex == 1 &&
      enrolmentsData.hasNext &&
      !enrolmentsData.isLoadingNext
    ) {
      enrolmentsData.loadNext(LOAD_X_PROFILES);
    }
  }, [accountsData, enrolmentsData, state.values.currentTabIndex]);

  // stop refetch useEffects firing on first render
  const renderCount = useRef(0);
  const environment = useRelayEnvironment();

  useDebounceFunction(
    () => {
      if (renderCount.current < 1) {
        renderCount.current += 1;
        return;
      }

      state.values.subscription?.unsubscribe();
      dispatchState({ input: "isRefetching", value: true });

      if (state.values.currentTabIndex == 0) {
        const variables = {
          accountType:
            detailType === "TEACHER_AND_DAY" || detailType === "TEACHER" ? "TEACHER" : "PUPIL",
          first: LOAD_X_PROFILES,
          orderBy: "user__firstName,user__lastName",
          schoolId:
            detailType === "TEACHER" || detailType === "TEACHER_AND_DAY" ? undefined : schoolId,
          searchTerm:
            state.values.searchTerm.trim() !== "" ? state.values.searchTerm.trim() : undefined,
        };

        dispatchState({
          input: "subscription",
          value: fetchQuery<LoadAccountsForModalQuery>(
            environment,
            load_accounts_for_modal,
            variables,
            {
              fetchPolicy: "store-or-network",
            }
          ).subscribe({
            complete: () => {
              dispatchState({
                input: "isRefetching",
                value: false,
              });
              accountsData.refetch(variables, {
                fetchPolicy: "store-only",
              });
            },
            error: () => {
              dispatchState({
                input: "isRefetching",
                value: false,
              });
            },
          }),
        });
      } else if (state.values.currentTabIndex == 1) {
        const variables = {
          excludeStatus: "ENROLED",
          first: LOAD_X_PROFILES,
          orderBy: "positionInWaitingList",
          schoolId,
          searchTerm:
            state.values.searchTerm.trim() !== "" ? state.values.searchTerm.trim() : undefined,
        };

        dispatchState({
          input: "subscription",
          value: fetchQuery<LoadEnrolmentsQuery>(environment, load_enrolments, variables, {
            fetchPolicy: "network-only",
          }).subscribe({
            complete: () => {
              dispatchState({
                input: "isRefetching",
                value: false,
              });
              enrolmentsData.refetch(variables, {
                fetchPolicy: "network-only",
              });
            },
            error: () => {
              dispatchState({
                input: "isRefetching",
                value: false,
              });
            },
          }),
        });
      }
    }, // no delay if clearing text
    state.values.searchTerm !== "" ? 750 : 0,

    [state.values.searchTerm, state.values.currentTabIndex, schoolId]
  );

  const refreshHandler = useCallback(() => {
    dispatchState({ input: "contentIsRendered", value: false });
    enrolmentsData.refetch(
      {
        first: LOAD_X_PROFILES,
        orderBy: "positionInWaitingList",
        searchTerm: state.values.searchTerm.trim(),
      },
      {
        fetchPolicy: "network-only",
      }
    );
  }, [enrolmentsData, state.values.searchTerm]);

  const inputChangeHandler = useCallback(
    (_: unknown, inputValue?: string) => {
      dispatchState({
        input: "searchTerm",
        value: inputValue,
      });
    },
    [dispatchState]
  );

  const inputClearHandler = useCallback(() => {
    if (state.values.searchTerm !== "") {
      dispatchState({
        input: "searchTerm",
        value: "",
      });
    }
  }, [dispatchState, state.values.searchTerm]);

  const checkboxChangeHandler = useCallback((isChecked: boolean) => {
    dispatchState({ input: "keepFree", value: isChecked });
  }, []);

  const updateLessonBlockDetailsHandler = useCallback(
    (variables: { keepFree?: boolean; pupilId?: string | null; teacherId?: string | null }) => {
      if (!lessonBlockIds?.length) {
        return;
      }

      updateLessonBlockDetails(
        {
          ...variables,
          lessonBlockIds,
        },
        undefined,
        () => {
          onHideModal();
          onUpdate?.();
        }
      );
    },
    [onHideModal, lessonBlockIds, onUpdate, updateLessonBlockDetails]
  );

  const checkIfAlreadyOffered = useCallback(
    (variables: { actionType: AlreadyOfferedAlertInfo["actionType"] }) => {
      const { actionType } = variables;

      // check if slot has already been offered to another pupil
      const activeOffer = lessonBlockDataForPupilChange?.enrolmentOffers?.edges.find(
        (edge) => edge?.node?.archived === false && edge?.node?.offerAccepted === false
      );

      if (activeOffer) {
        dispatchState({
          input: "alreadyOfferedAlertInfo",
          value: {
            actionType,
            isOpen: true,
            type:
              activeOffer.node?.enrolment.id === state.values.selectedProfileInfo?.id
                ? "SLOT_ALREADY_OFFERED_TO_THIS_PUPIL"
                : "SLOT_ALREADY_OFFERED",
          },
        });
        return true;
      }

      // check if the selected pupil has already been offered a slot
      const selectedProfileInfoEnrolmentStatus = state.values.selectedProfileInfo?.enrolmentStatus;

      if (selectedProfileInfoEnrolmentStatus === "OFFERED") {
        dispatchState({
          input: "alreadyOfferedAlertInfo",
          value: {
            actionType,
            isOpen: true,
            type: "PUPIL_ALREADY_OFFERED_OTHER_SLOT",
          },
        });
        return true;
      }

      return false;
    },
    [lessonBlockDataForPupilChange, state.values.selectedProfileInfo]
  );

  const addOrRemoveTeacherOrExistingPupil = useCallback(
    (variables?: { skipEnrolmentStatusCheck?: boolean }) => {
      if (detailType === "PUPIL") {
        if (
          !variables?.skipEnrolmentStatusCheck &&
          (lessonBlockDataForPupilChange?.pupil?.id ||
            lessonBlockDataForPupilChange?.enrolmentOffers?.edges?.some(
              (edge) => edge?.node?.offerAccepted === false && edge?.node?.archived === false
            ))
        ) {
          const alreadyOffered = checkIfAlreadyOffered({
            actionType: state.values.selectedProfileInfo?.id
              ? "ADD_EXISTING_PUPIL"
              : "REMOVE_PUPIL",
          });

          if (alreadyOffered) {
            return;
          }
        }

        updateLessonBlockDetailsHandler({
          keepFree: !state.values.selectedProfileInfo?.id ? state.values.keepFree : false,
          pupilId: state.values.selectedProfileInfo?.id ?? null,
        });
      } else if (detailType === "TEACHER") {
        updateLessonBlockDetailsHandler({
          keepFree: !state.values.selectedProfileInfo?.id ? state.values.keepFree : false,
          teacherId: state.values.selectedProfileInfo?.id ?? null,
        });
      } else if (detailType === "TEACHER_AND_DAY") {
        dispatchState({
          input: "dayPickerAfterTeacherIsOpen",
          value: true,
        });
      }
    },
    [
      detailType,
      lessonBlockDataForPupilChange?.pupil?.id,
      lessonBlockDataForPupilChange?.enrolmentOffers?.edges,
      updateLessonBlockDetailsHandler,
      state.values.selectedProfileInfo?.id,
      state.values.keepFree,
      checkIfAlreadyOffered,
    ]
  );

  const navigateToSlotOfferMessageHandler = useCallback(
    (variables?: { skipEnrolmentStatusCheck?: boolean }) => {
      const pupilFirstName = state.values.selectedProfileInfo?.firstName;
      const parentName = state.values.selectedProfileInfo?.parentName;

      if (!parentName || !pupilFirstName || !lessonBlockDataForPupilChange) {
        return;
      }

      if (!variables?.skipEnrolmentStatusCheck) {
        const alreadyOffered = checkIfAlreadyOffered({
          actionType: "OFFER_SLOT",
        });

        if (alreadyOffered) {
          return;
        }
      }

      dispatchState({
        input: "messageString",
        value: getSlotOfferMessageString({
          lessonBlockData: lessonBlockDataForPupilChange,
          parentName,
          pupilFirstName,
        }),
      });
      dispatchState({
        input: "messageSubject",
        value: getSlotOfferMessageSubject({
          pupilFirstName,
        }),
      });
      dispatchState({
        input: "currentPageIndex",
        value: 1,
      });
    },
    [
      state.values.selectedProfileInfo?.firstName,
      state.values.selectedProfileInfo?.parentName,
      lessonBlockDataForPupilChange,
      checkIfAlreadyOffered,
    ]
  );

  const offerLessonBlockToNewPupil = useCallback(() => {
    const enrolmentId = state.values.selectedProfileInfo?.id;
    const lessonBlockId = lessonBlockIds?.[0];
    const messageString = state.values.messageString;
    const messageSubject = state.values.messageSubject;

    if (!lessonBlockId || !enrolmentId || !messageString || !messageSubject) {
      return;
    }

    const offerLessonBlockToNewPupilConfig = {
      variables: {
        input: {
          archiveExistingOffer: state.values.archiveExistingOfferOnOfferSlot,
          enrolmentId,
          lessonBlockId,
          messageString,
          messageSubject,
        },
      },
      onCompleted: (response: OfferLessonBlockToNewPupilMutation$data) => {
        if (response?.offerLessonBlockToNewPupil?.success) {
          toast.show({
            render: ({ id }: { id: string }) => (
              <ToastAlert
                id={id}
                status="success"
                title={"Successfully offered slot"}
                toast={toast}
              />
            ),
          });

          onHideModal();
        } else {
          toast.show({
            render: ({ id }: { id: string }) => (
              <ToastAlert
                description={"Please get in touch with one of the team"}
                id={id}
                status="error"
                title={"Failed to offer slot"}
                toast={toast}
              />
            ),
          });
        }
      },
    };

    commitOfferLessonBlockToNewPupil(offerLessonBlockToNewPupilConfig);
  }, [
    commitOfferLessonBlockToNewPupil,
    lessonBlockIds,
    onHideModal,
    state.values.archiveExistingOfferOnOfferSlot,
    state.values.messageString,
    state.values.messageSubject,
    state.values.selectedProfileInfo?.id,
    toast,
  ]);

  const enrolPupil = useCallback(
    (variables?: { skipEnrolmentStatusCheck?: boolean }) => {
      const enrolmentId = state.values.selectedProfileInfo?.id;
      const lessonBlockId = lessonBlockIds?.[0];

      if (!enrolmentId || !lessonBlockId) {
        return;
      }

      if (!variables?.skipEnrolmentStatusCheck) {
        const alreadyOffered = checkIfAlreadyOffered({
          actionType: "ENROL_DIRECTLY",
        });

        if (alreadyOffered) {
          return;
        }
      }

      const enrolPupilConfig = {
        variables: {
          input: {
            enrolmentId,
            lessonBlockId,
          },
        },
        onCompleted: (response: EnrolPupilMutation$data) => {
          if (response?.enrolPupil?.success) {
            refreshHandler();

            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="success"
                  title={"Successfully enroled pupil"}
                  toast={toast}
                />
              ),
            });

            onHideModal();
            onUpdate?.();
          } else {
            const { description, title } = getEnrolErrorToastText(response);

            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={description}
                  id={id}
                  status="error"
                  title={title}
                  toast={toast}
                />
              ),
            });
          }
        },
      };

      commitEnrolPupil(enrolPupilConfig);
    },
    [
      checkIfAlreadyOffered,
      commitEnrolPupil,
      lessonBlockIds,
      onHideModal,
      onUpdate,
      refreshHandler,
      state.values.selectedProfileInfo?.id,
      toast,
    ]
  );

  const selectAccountHandler = useCallback(
    (data: NonNullable<NonNullable<AccountModalItem>["node"]>, fullName: string) => {
      const rowRef = rowRefs.current.get(data.id);

      if (rowRef?.getIsChecked()) {
        dispatchState({
          input: "selectedProfileInfo",
          value: {
            fullName,
            id: data.id,
          },
        });
        dispatchState({
          input: "keepFree",
          value: false,
        });
      } else {
        dispatchState({
          input: "selectedProfileInfo",
          value: undefined,
        });
      }
      // deselect all the other rows
      rowRefs.current.forEach((ref, id) => {
        if (ref && id !== data.id) {
          ref.setIsChecked(false);
        }
      });
    },
    []
  );

  const selectEnrolmentHandler = useCallback(
    (data: NonNullable<NonNullable<EnrolmentItem>["node"]>, fullName: string) => {
      const rowRef = rowRefs.current.get(data.id);

      if (rowRef?.getIsChecked()) {
        dispatchState({
          input: "selectedProfileInfo",
          value: {
            enrolmentStatus: data.status,
            firstName: data.pupilFirstName,
            fullName,
            id: data.id,
            parentName: data.parentName,
          },
        });
        dispatchState({
          input: "keepFree",
          value: false,
        });
      } else {
        dispatchState({
          input: "selectedProfileInfo",
          value: undefined,
        });
      }
      // deselect all the other rows
      rowRefs.current.forEach((ref, id) => {
        if (ref && id !== data.id) {
          ref.setIsChecked(false);
        }
      });
    },
    []
  );

  const messageFinishEditingHandler = useCallback(
    (inputIdentifier?: string | number, inputValue?: string | undefined) => {
      if (typeof inputValue === "string" && typeof inputIdentifier === "string") {
        dispatchState({
          input: inputIdentifier as keyof ReducerValues,
          value: inputValue,
        });
      }
    },
    []
  );

  const accountsRowRenderer = useCallback<RecyclerListViewProps["rowRenderer"]>(
    (_, item: AccountModalItem) => {
      if (!item?.node) {
        return null;
      }

      return (
        <AccountSelectRow
          ref={(ref) => {
            if (ref && item.node?.id && !rowRefs.current.get(item.node.id)) {
              rowRefs.current.set(item.node.id, ref);
            }
          }}
          data={item.node}
          detailType={detailType}
          initiallySelected={item.node.id === state.values.selectedProfileInfo?.id}
          pressItemHandler={(data) => {
            onHideModal();
            navigation.push("Account", {
              accountType: detailType === "PUPIL" ? "PUPIL" : "TEACHER",
              block: selectedBlock,
              profileId: data.id,
              startingYear: selectedStartingYear,
            });
          }}
          schoolId={schoolId}
          selectItemHandler={selectAccountHandler}
        />
      );
    },
    [
      detailType,
      state.values.selectedProfileInfo?.id,
      schoolId,
      selectAccountHandler,
      onHideModal,
      navigation,
      selectedStartingYear,
      selectedBlock,
    ]
  );

  const waitingListRowRenderer = useCallback<RecyclerListViewProps["rowRenderer"]>(
    (_, item: EnrolmentItem) => {
      if (!item?.node) {
        return null;
      }

      return (
        <EnrolmentSelectRow
          ref={(ref) => {
            if (ref && item.node?.id && !rowRefs.current.get(item.node.id)) {
              rowRefs.current.set(item.node.id, ref);
            }
          }}
          data={item.node}
          detailType={detailType}
          initiallySelected={item.node.id === state.values.selectedProfileInfo?.id}
          pressItemHandler={(data) => {
            onHideModal();
            navigation.push("Enrolment", {
              enrolmentId: data.id,
            });
          }}
          selectItemHandler={selectEnrolmentHandler}
        />
      );
    },
    [
      detailType,
      navigation,
      onHideModal,
      selectEnrolmentHandler,
      state.values.selectedProfileInfo?.id,
    ]
  );

  const renderFooter = useCallback(() => {
    if (accountsData.isLoadingNext || enrolmentsData.isLoadingNext) {
      return <CustomFlatListSpinner position="relative" py="4" top="0" />;
    }

    return null;
  }, [accountsData.isLoadingNext, enrolmentsData.isLoadingNext]);

  const renderRefetchIndicator = useMemo(() => {
    return state.values.isRefetching ? <CustomFlatListSpinner top="4" /> : null;
  }, [state.values.isRefetching]);

  const renderAccountsSearchResults = useMemo(() => {
    return state.values.dataProvider.getSize() > 0 ? (
      <RecyclerListView
        dataProvider={state.values.dataProvider}
        layoutProvider={layoutProvider}
        onEndReached={onSearchEndReached}
        onEndReachedThreshold={100}
        renderFooter={renderFooter}
        rowRenderer={accountsRowRenderer}
        scrollViewProps={{
          contentContainerStyle: { marginBottom: 16 },
        }}
        style={{
          flex: 1,
          minHeight: 1,
        }}
        suppressBoundedSizeException
        useWindowScroll
      />
    ) : !state.values.contentIsRendered ? (
      <LoadingBlobs bg="transparent" textProps={{ color: "surface.900" }}>
        Loading...
      </LoadingBlobs>
    ) : !state.values.isRefetching ? (
      <Center flex={1} flexGrow={1}>
        <PresenceTransition
          animate={{
            opacity: 1,
            transition: {
              delay: 500,
              duration: 300,
            },
          }}
          initial={{
            opacity: 0,
          }}
          visible={true}
        >
          <ListEmptyBanner explainer={"No accounts found..."}>
            <Text fontSize="6xl">{"😐"}</Text>
          </ListEmptyBanner>
        </PresenceTransition>
      </Center>
    ) : null;
  }, [
    state.values.dataProvider,
    state.values.isRefetching,
    state.values.contentIsRendered,
    accountsRowRenderer,
    layoutProvider,
    onSearchEndReached,
    renderFooter,
  ]);

  const renderWaitingListSearchResults = useMemo(() => {
    if (detailType !== "PUPIL" || state.values.currentTabIndex !== 1) {
      return null;
    }

    return state.values.dataProvider.getSize() > 0 ? (
      <RecyclerListView
        dataProvider={state.values.dataProvider}
        layoutProvider={layoutProvider}
        onEndReached={onSearchEndReached}
        onEndReachedThreshold={100}
        renderFooter={renderFooter}
        rowRenderer={waitingListRowRenderer}
        scrollViewProps={{
          contentContainerStyle: { marginBottom: 16 },
        }}
        style={{
          flex: 1,
          minHeight: 1,
        }}
        suppressBoundedSizeException
        useWindowScroll
      />
    ) : !state.values.contentIsRendered ? (
      <LoadingBlobs bg="transparent" textProps={{ color: "surface.900" }}>
        Loading...
      </LoadingBlobs>
    ) : !state.values.isRefetching ? (
      <Center flex={1} flexGrow={1}>
        <PresenceTransition
          animate={{
            opacity: 1,
            transition: {
              delay: 500,
              duration: 300,
            },
          }}
          initial={{
            opacity: 0,
          }}
          visible={true}
        >
          <ListEmptyBanner explainer={"No enrolments found..."}>
            <Text fontSize="6xl">{"😐"}</Text>
          </ListEmptyBanner>
        </PresenceTransition>
      </Center>
    ) : null;
  }, [
    detailType,
    state.values.currentTabIndex,
    state.values.dataProvider,
    state.values.contentIsRendered,
    state.values.isRefetching,
    layoutProvider,
    onSearchEndReached,
    renderFooter,
    waitingListRowRenderer,
  ]);

  const renderSelectedProfileInfoCard = useMemo(() => {
    return (
      <VStack alignItems="center" space="2" width="100%">
        <Text color="surface.100" fontSize="md">
          {`Selected ${detailTypeText}`}
        </Text>
        <HStack
          bg={state.values.selectedProfileInfo ? "primary.500" : "muted.400"}
          borderRadius="2xl"
          height="60px"
          maxWidth="70%"
          px="4"
          py="3"
          shadow={1}
        >
          {state.values.selectedProfileInfo ? (
            <>
              <Text
                color="surface.100"
                fontFamily="Poppins-Regular"
                fontSize="md"
                isTruncated
                numberOfLines={1}
              >
                {state.values.selectedProfileInfo.fullName}
              </Text>
              <Button
                _hover={{
                  bg: "transparent",
                  opacity: 0.8,
                }}
                _pressed={{
                  bg: "transparent",
                  opacity: 0.7,
                }}
                leftIcon={<CloseIcon color="surface.100" size="6" />}
                ml="2"
                onPress={() => {
                  dispatchState({
                    input: "selectedProfileInfo",
                    value: undefined,
                  });
                  rowRefs.current.forEach((ref) => {
                    ref?.setIsChecked(false);
                  });
                }}
                p="1"
                variant="ghost"
              />
            </>
          ) : (
            <Text color="surface.100" fontFamily="Poppins-Regular" fontSize="md">
              {`No ${detailTypeText} Selected`}
            </Text>
          )}
        </HStack>
      </VStack>
    );
  }, [detailTypeText, state.values.selectedProfileInfo]);

  const searchBarRef = useRef(null);

  useEffect(() => {
    setTimeout(() => {
      dispatchState({ input: "contentIsRendered", value: true });
    }, 1000);
    // unsubscribe from refetch on unmount
    return () => state.values.subscription?.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderAccounts = useMemo(() => {
    if (state.values.currentPageIndex === 1) {
      return null;
    }

    return (
      <>
        <VStack flex={1} space="6" width="85%">
          <SearchBar
            ref={searchBarRef}
            inputClearHandler={inputClearHandler}
            inputOnChangeHandler={inputChangeHandler}
            inputSearchHandler={inputChangeHandler}
            placeholderText={`Search for a ${detailTypeText.toLowerCase()}`}
            searchText={state.values.searchTerm}
            showSearchIcon
          />
          <Box bg="surface.100" borderRadius="2xl" height="300px" overflow="hidden" width="100%">
            {renderAccountsSearchResults}
            {renderRefetchIndicator}
          </Box>
          <VStack alignItems="center" space="6">
            {renderSelectedProfileInfoCard}
            {detailType === "PUPIL" ? (
              <PresenceTransition
                animate={{
                  opacity: 1,
                  transition: {
                    duration: 500,
                  },
                }}
                initial={{
                  opacity: 0,
                }}
                visible={!state.values.selectedProfileInfo}
              >
                <HStack space="4">
                  <Text color="surface.100" fontSize="md">
                    {"Mark slot as "}
                    <Text fontStyle="italic">keep free</Text>
                  </Text>
                  <Checkbox
                    defaultIsChecked={state.values.keepFree}
                    onChange={checkboxChangeHandler}
                    size="lg"
                    value="keepFree"
                  />
                </HStack>
              </PresenceTransition>
            ) : null}
          </VStack>
        </VStack>
        <ButtonDebounced
          _text={{ fontSize: "xl" }}
          height="56px"
          isLoading={updateLessonBlockDetailsInFlight}
          mb="4"
          mt="8"
          onPress={() => addOrRemoveTeacherOrExistingPupil()}
          width="180px"
        >
          {detailType !== "TEACHER_AND_DAY" ? "Save" : "Next"}
        </ButtonDebounced>
      </>
    );
  }, [
    state.values.currentPageIndex,
    state.values.searchTerm,
    state.values.selectedProfileInfo,
    state.values.keepFree,
    inputClearHandler,
    inputChangeHandler,
    detailTypeText,
    renderAccountsSearchResults,
    renderRefetchIndicator,
    renderSelectedProfileInfoCard,
    detailType,
    checkboxChangeHandler,
    updateLessonBlockDetailsInFlight,
    addOrRemoveTeacherOrExistingPupil,
  ]);

  const renderWaitingList = useMemo(() => {
    if (detailType !== "PUPIL" || state.values.currentTabIndex !== 1) {
      return null;
    }

    const slotOfferIsDisabled = // do not allow offering slots to pupils with no parent
      !state.values.selectedProfileInfo?.parentName ||
      !state.values.selectedProfileInfo?.firstName ||
      !lessonBlockDataForPupilChange;

    return (
      <>
        <VStack flex={1} space="6" width="85%">
          <SearchBar
            ref={searchBarRef}
            inputClearHandler={inputClearHandler}
            inputOnChangeHandler={inputChangeHandler}
            inputSearchHandler={inputChangeHandler}
            placeholderText={"Search for a pupil"}
            searchText={state.values.searchTerm}
            showSearchIcon
          />
          <Box bg="surface.100" borderRadius="2xl" height="300px" overflow="hidden" width="100%">
            {renderWaitingListSearchResults}
            {renderRefetchIndicator}
          </Box>
          <VStack alignItems="center" space="6">
            {renderSelectedProfileInfoCard}
            <PresenceTransition
              animate={{
                opacity: 1,
                transition: {
                  duration: 500,
                },
              }}
              initial={{
                opacity: 0,
              }}
              visible={!state.values.selectedProfileInfo}
            >
              <HStack space="4">
                <Text color="surface.100" fontSize="md">
                  {"Mark slot as "}
                  <Text fontStyle="italic">keep free</Text>
                </Text>
                <Checkbox
                  defaultIsChecked={state.values.keepFree}
                  onChange={checkboxChangeHandler}
                  size="lg"
                  value="keepFree"
                />
              </HStack>
            </PresenceTransition>
          </VStack>
        </VStack>
        {state.values.selectedProfileInfo ? (
          <HStack space="4">
            <ButtonDebounced
              _text={{ fontSize: "xl" }}
              colorScheme="surface"
              height="56px"
              isLoading={enrolPupilInFlight}
              mb="4"
              mt="8"
              onPress={() => enrolPupil()}
              width="180px"
            >
              {"Enrol Directly"}
            </ButtonDebounced>
            <ButtonDebounced
              _text={{ fontSize: "xl" }}
              disabled={slotOfferIsDisabled}
              height="56px"
              mb="4"
              mt="8"
              onPress={() => navigateToSlotOfferMessageHandler()}
              opacity={slotOfferIsDisabled ? 0.5 : 1}
              width="180px"
            >
              {"Offer Slot"}
            </ButtonDebounced>
          </HStack>
        ) : (
          <ButtonDebounced
            _text={{ fontSize: "xl" }}
            height="56px"
            isLoading={updateLessonBlockDetailsInFlight}
            mb="4"
            mt="8"
            onPress={() => addOrRemoveTeacherOrExistingPupil()}
            width="180px"
          >
            {"Save"}
          </ButtonDebounced>
        )}
      </>
    );
  }, [
    detailType,
    state.values.currentTabIndex,
    state.values.searchTerm,
    state.values.selectedProfileInfo,
    state.values.keepFree,
    inputClearHandler,
    inputChangeHandler,
    renderWaitingListSearchResults,
    renderRefetchIndicator,
    renderSelectedProfileInfoCard,
    checkboxChangeHandler,
    enrolPupilInFlight,
    lessonBlockDataForPupilChange,
    updateLessonBlockDetailsInFlight,
    enrolPupil,
    navigateToSlotOfferMessageHandler,
    addOrRemoveTeacherOrExistingPupil,
  ]);

  const renderMessageInput = useMemo(() => {
    if (detailType !== "PUPIL" || state.values.currentPageIndex === 0) {
      return null;
    }

    return (
      <>
        <VStack alignItems="center" flex={1} space="6" width="95%">
          <Text color="surface.400" mx="6" textAlign="center">
            {"Customise the SMS/email that will be sent to the pupil's parent to offer the slot."}
          </Text>
          <TextInput
            ref={messageSubjectInputRef}
            _focus={{ bg: "white" }}
            _hover={{ bg: "white" }}
            autoCapitalize="none"
            bg="white"
            borderRadius="xl"
            fontFamily="Poppins-Regular"
            id="messageSubject"
            initiallyValid
            initialValue={state.values.messageSubject}
            keyboardType="default"
            label="Subject (not sent with SMS)"
            labelStyle={{
              color: "surface.400",
              fontSize: "sm",
            }}
            onFinishEditing={messageFinishEditingHandler}
            onSubmit={() => messageStringInputRef.current?.focus()}
            py="1.5"
            size="lg"
          />
          <TextInput
            ref={messageStringInputRef}
            _focus={{ bg: "white" }}
            _hover={{ bg: "white" }}
            autoCapitalize="sentences"
            bg="white"
            borderRadius="xl"
            fontFamily="Poppins-Regular"
            height="300px"
            id="messageString"
            initialValue={state.values.messageString}
            keyboardType="default"
            label="Message"
            labelStyle={{
              color: "surface.400",
              fontSize: "sm",
            }}
            multiline
            onFinishEditing={messageFinishEditingHandler}
            size="lg"
          />
        </VStack>
        <ButtonDebounced
          _text={{ fontSize: "xl" }}
          height="56px"
          isDisabled={!state.values.messageString || !state.values.messageSubject}
          isLoading={offerLessonBlockToNewPupilInFlight}
          mb="4"
          mt="10"
          onPress={offerLessonBlockToNewPupil}
          width="220px"
        >
          {"Send SMS & Email"}
        </ButtonDebounced>
      </>
    );
  }, [
    detailType,
    messageFinishEditingHandler,
    offerLessonBlockToNewPupil,
    offerLessonBlockToNewPupilInFlight,
    state.values.currentPageIndex,
    state.values.messageString,
    state.values.messageSubject,
  ]);

  const renderAlreadyOfferedAlert = useMemo(() => {
    if (detailType !== "PUPIL") {
      return null;
    }

    const onClose = () => {
      dispatchState({
        input: "alreadyOfferedAlertInfo",
        value: {
          ...state.values.alreadyOfferedAlertInfo,
          isOpen: false,
        },
      });
    };

    let header: string;
    let body: string;

    if (state.values.alreadyOfferedAlertInfo.type === "PUPIL_ALREADY_OFFERED_OTHER_SLOT") {
      header = "This pupil has already been offered a slot";
      body =
        "Continuing will archive the existing offer for the other slot.\n\nAre you sure you want to proceed?";
    } else if (state.values.alreadyOfferedAlertInfo.type === "SLOT_ALREADY_OFFERED_TO_THIS_PUPIL") {
      header = "This slot has already been offered to this pupil";
      body = "Are you sure you want to offer the slot to this pupil again?";
    } else {
      header = "This slot has already been offered to another pupil";
      body =
        "Continuing will archive the existing offer made to the other pupil.\n\nAre you sure you want to proceed?";
    }

    let confirmButtonText: string;
    let confirmButtonAction: () => void;
    if (state.values.alreadyOfferedAlertInfo.actionType === "OFFER_SLOT") {
      confirmButtonText = "Yes, offer the slot";
      confirmButtonAction = () => {
        dispatchState({
          input: "archiveExistingOfferOnOfferSlot",
          value: true,
        });
        navigateToSlotOfferMessageHandler({
          skipEnrolmentStatusCheck: true,
        });
      };
    } else if (state.values.alreadyOfferedAlertInfo.actionType === "ENROL_DIRECTLY") {
      confirmButtonText = "Yes, enrol pupil";
      confirmButtonAction = () => {
        enrolPupil({ skipEnrolmentStatusCheck: true });
      };
    } else if (state.values.alreadyOfferedAlertInfo.actionType === "ADD_EXISTING_PUPIL") {
      confirmButtonText = "Yes, add pupil";
      confirmButtonAction = () => {
        addOrRemoveTeacherOrExistingPupil({
          skipEnrolmentStatusCheck: true,
        });
      };
    } else {
      confirmButtonText = "Yes, remove pupil";
      confirmButtonAction = () => {
        addOrRemoveTeacherOrExistingPupil({
          skipEnrolmentStatusCheck: true,
        });
      };
    }

    return (
      <AlertPopup
        alertIsOpen={state.values.alreadyOfferedAlertInfo.isOpen}
        body={body}
        header={header}
        setAlertIsOpen={onClose}
      >
        <Button
          _text={{ fontSize: "lg" }}
          colorScheme="surface"
          height="80px"
          minWidth="56"
          onPress={onClose}
          width="25%"
        >
          {"No, go back"}
        </Button>
        <ButtonDebounced
          _text={{ fontSize: "lg" }}
          height="80px"
          minWidth="56"
          onPress={() => {
            onClose();
            confirmButtonAction();
          }}
          width="25%"
        >
          {confirmButtonText}
        </ButtonDebounced>
      </AlertPopup>
    );
  }, [
    detailType,
    state.values.alreadyOfferedAlertInfo,
    navigateToSlotOfferMessageHandler,
    enrolPupil,
    addOrRemoveTeacherOrExistingPupil,
  ]);

  if (detailType === null) {
    return null;
  }

  return (
    <>
      <Modal
        initialFocusRef={searchBarRef}
        isOpen={showModal && !state.values.dayPickerAfterTeacherIsOpen}
        onClose={() => {
          onHideModal();
          dispatchState({ input: "searchTerm", value: "" });
          dispatchState({
            input: "currentPageIndex",
            value: 0,
          });
        }}
        size="xl"
      >
        <Modal.Content bg="primary.800" maxHeight="92%" minWidth="700px">
          {state.values.currentPageIndex > 0 ? (
            <Button
              _hover={{ bg: "transparent", opacity: 0.8 }}
              _pressed={{ bg: "transparent", opacity: 0.7 }}
              bg="transparent"
              left="3"
              leftIcon={<ChevronLeftIcon color="surface.100" ml="0" p="0" size="5" />}
              onPress={() => {
                dispatchState({
                  input: "archiveExistingOfferOnOfferSlot",
                  value: false,
                });
                dispatchState({
                  input: "currentPageIndex",
                  value: 0,
                });
                rowRefs.current = new Map();
              }}
              p="1"
              position="absolute"
              top="4"
              variant="ghost"
              zIndex="1"
            />
          ) : null}
          <Modal.CloseButton
            _hover={{ bg: "transparent", opacity: 0.7 }}
            _icon={{ color: "surface.100" }}
            _pressed={{ bg: "transparent", opacity: 0.7 }}
          />
          <Modal.Header _text={{ color: "surface.100" }} bg="primary.800">
            {state.values.currentPageIndex === 0 || detailType !== "PUPIL"
              ? `Select ${detailTypeText}`
              : "Offer Slot"}
          </Modal.Header>
          <Modal.Body alignItems="center" mb="16px" px="8px">
            <>
              {detailType === "PUPIL" ? (
                state.values.currentPageIndex === 0 ? (
                  <>
                    <TopTabBar
                      bg="primary.100"
                      borderRadius="lg"
                      buttonStyle={{
                        _hover: { bg: "primary.200" },
                        _pressed: { bg: "primary.300" },
                        alignItems: "center",
                        bg: "primary.50",
                        pb: 2,
                        pt: 2,
                      }}
                      currentPageIndex={state.values.currentTabIndex}
                      hideBottomBar
                      mt="4"
                      mx="12"
                      onChangePage={() => {
                        // clear the selected account when changing tabs
                        // (unless it was already selected initially)
                        if (
                          initiallySelectedProfileInfo?.id !== state.values.selectedProfileInfo?.id
                        ) {
                          dispatchState({
                            input: "selectedProfileInfo",
                            value: undefined,
                          });
                        }
                      }}
                      overflow="hidden"
                      pages={["Enroled", "Waiting List"]}
                      selectedButtonStyle={{
                        bg: "primary.100",
                      }}
                      selectedTextStyle={{
                        color: "primary.600",
                      }}
                      setCurrentPageIndex={(index) => {
                        dispatchState({
                          input: "currentTabIndex",
                          value: index,
                        });
                      }}
                      shadow={1}
                      textStyle={{ color: "primary.800" }}
                    />
                    {state.values.currentTabIndex === 0 && (
                      <Box
                        alignItems={"center"}
                        flex={1}
                        justifyContent={"center"}
                        mt="6"
                        width="100%"
                      >
                        {renderAccounts}
                      </Box>
                    )}
                    {state.values.currentTabIndex === 1 && (
                      <Box
                        alignItems={"center"}
                        flex={1}
                        justifyContent={"center"}
                        mt="6"
                        width="100%"
                      >
                        {renderWaitingList}
                      </Box>
                    )}
                  </>
                ) : (
                  renderMessageInput
                )
              ) : detailType == "TEACHER" || detailType == "TEACHER_AND_DAY" ? (
                <>
                  <Box alignItems={"center"} flex={1} justifyContent={"center"} mt="6" width="100%">
                    {renderAccounts}
                  </Box>
                </>
              ) : null}
            </>
          </Modal.Body>
        </Modal.Content>
      </Modal>
      {updateScheduledLessonBlockTime && lessonBlockIds?.length ? (
        <DayPickerModal
          hideModal={() => {
            dispatchState({
              input: "dayPickerAfterTeacherIsOpen",
              value: false,
            });
            onHideModal();
          }}
          initialLessonDay={dayIndex}
          isAfterAssignTeacherStep
          lessonBlockIds={lessonBlockIds}
          lessonBlocksConnectionId={lessonBlocksConnectionId}
          onUpdate={onUpdate}
          selectedBlock={selectedBlock ?? 0}
          selectedProfileId={state.values.selectedProfileInfo?.id ?? ""}
          selectedStartingYear={selectedStartingYear ?? 0}
          showModal={Boolean(state.values.dayPickerAfterTeacherIsOpen)}
          updateScheduledLessonBlockTime={updateScheduledLessonBlockTime}
          updateScheduledLessonBlockTimeInFlight={updateScheduledLessonBlockTimeInFlight ?? false}
        />
      ) : null}
      {detailType === "PUPIL" ? renderAlreadyOfferedAlert : null}
    </>
  );
};

const UpdateLessonBlockTeacherOrPupilModalWrapper = (props: WrapperProps) => {
  const { detailType, schoolId } = props;

  const [loadTeachersQueryReference, loadTeachersQuery] =
    useQueryLoader<LoadAccountsForModalQuery>(load_accounts_for_modal);

  const [loadPupilsQueryReference, loadPupilsQuery] =
    useQueryLoader<LoadAccountsForModalQuery>(load_accounts_for_modal);

  const [loadEnrolmentsQueryReference, loadEnrolmentsQuery] =
    useQueryLoader<LoadEnrolmentsQuery>(load_enrolments);

  useEffect(() => {
    loadTeachersQuery(
      {
        accountType: "TEACHER",
        first: LOAD_X_PROFILES,
        orderBy: "user__firstName,user__lastName",
      },
      { fetchPolicy: "store-or-network" }
    );
    loadPupilsQuery(
      {
        accountType: "PUPIL",
        first: LOAD_X_PROFILES,
        orderBy: "user__firstName,user__lastName",
        schoolId,
      },
      { fetchPolicy: "store-or-network" }
    );
    loadEnrolmentsQuery(
      {
        excludeStatus: "ENROLED",
        first: LOAD_X_PROFILES,
        orderBy: "positionInWaitingList",
        schoolId,
      },
      { fetchPolicy: "store-or-network" }
    );
  }, [loadPupilsQuery, loadTeachersQuery, loadEnrolmentsQuery, schoolId]);

  return loadTeachersQueryReference &&
    loadPupilsQueryReference &&
    loadEnrolmentsQueryReference &&
    detailType ? (
    <Suspense fallback={<></>}>
      <UpdateLessonBlockTeacherOrPupilModal
        loadEnrolmentsQueryReference={loadEnrolmentsQueryReference}
        loadPupilsQueryReference={loadPupilsQueryReference}
        loadTeachersQueryReference={loadTeachersQueryReference}
        {...props}
      />
    </Suspense>
  ) : (
    <></>
  );
};

export default React.memo(UpdateLessonBlockTeacherOrPupilModalWrapper);
